/**
 * @file src/pages/company-settings/useUpdateCompany.js
 * @description React Query mutation hook for updating company details.
 *
 * What changed from the original:
 * - updateCompany imported from api/company.api.js
 *  - Query keys use QUERY_KEYS constants not hardcoded strings
 *  - onSuccess/onError removed from hook — caller handles toast/feedback
 *  - console.log/error removed
 *
 * Usage:
 *  const { mutate: updateCompany, isLoading } = useUpdateCompany();
 *  mutate(formData, {
 *    onSuccess: () => toast.success("Company updated"),
 *    onError:   (err) => toast.error(err.message),
 *  });
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateCompany } from "api/company.api";
import { QUERY_KEYS } from "config/queryKeys";

export const useUpdateCompany = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => updateCompany(data),
    onSuccess: () => {
      // Invalidate company and devices cache so UI reflects changes
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.company.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.devices.all });
    },
  });
};
